import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UI, btnSx } from "../components/ActionDrawer";
import ReviewExpenseClaimModal from "../components/ReviewExpenseClaimModal";
import DashTable, { PageHead, SectionTitle, Tag } from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

const rupee = (paise) =>
  paise === null || paise === undefined
    ? "—"
    : `₹${(Number(paise) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const PAGE_SIZE = 15;

const Pager = ({ pagination, onPrev, onNext }) => {
  if (!pagination || pagination.total <= 0) return null;
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "10px",
        margin: "10px 0 20px",
        fontSize: 13,
        color: UI.ink3,
      }}
    >
      <span>
        {(pagination.page - 1) * pagination.limit + 1}–
        {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
      </span>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <span>
          Page {pagination.page} of {pagination.totalPages}
        </span>
        <Box sx={{ display: "flex", gap: "8px" }}>
          <Box
            component="button"
            type="button"
            onClick={onPrev}
            disabled={pagination.page <= 1}
            sx={btnSx("ghost", pagination.page <= 1)}
          >
            Previous
          </Box>
          <Box
            component="button"
            type="button"
            onClick={onNext}
            disabled={pagination.page >= pagination.totalPages}
            sx={btnSx("ghost", pagination.page >= pagination.totalPages)}
          >
            Next
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

/* Flow 2 (review queue) + Flow 4 (payment run). No multi-select pattern
   exists anywhere else in this app, so the payment section below — a
   checkbox per row plus a sticky bulk-action bar — is built from scratch,
   kept visually consistent with the existing table/button styling. */
const ExpenseApprovals = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [queue, setQueue] = useState([]);
  const [queuePage, setQueuePage] = useState(1);
  const [queuePagination, setQueuePagination] = useState(null);
  const [approved, setApproved] = useState([]);
  const [approvedPage, setApprovedPage] = useState(1);
  const [approvedPagination, setApprovedPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [active, setActive] = useState(null);

  // Keyed by claim id, holding the claim itself — not just a Set of ids —
  // so a claim selected on one page of the payment run still contributes to
  // the total/count after paging away from it.
  const [selectedMap, setSelectedMap] = useState(new Map());
  const [reference, setReference] = useState("");
  const [paying, setPaying] = useState(false);
  const [approvingId, setApprovingId] = useState(null);

  const fetchQueue = async (targetPage = queuePage) => {
    try {
      const res = await apiService.fetchExpenseQueue(token, { page: targetPage, limit: PAGE_SIZE });
      setQueue(res.data || []);
      setQueuePagination(res.pagination || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching expense claims");
    }
  };

  const fetchApproved = async (targetPage = approvedPage) => {
    try {
      const res = await apiService.fetchExpenseQueue(token, {
        status: "approved",
        page: targetPage,
        limit: PAGE_SIZE,
      });
      setApproved(res.data || []);
      setApprovedPagination(res.pagination || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching expense claims");
    }
  };

  // Re-fetches both lists at their current pages — used after an action
  // (approve/pay) moves a claim from one list to the other.
  const fetchAll = async () => {
    setIsLoading(true);
    setLoadFailed(false);
    await Promise.all([fetchQueue(queuePage), fetchApproved(approvedPage)]);
    setIsLoading(false);
  };

  useEffect(() => {
    setIsLoading(true);
    fetchQueue(queuePage).finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queuePage]);

  useEffect(() => {
    setIsLoading(true);
    fetchApproved(approvedPage).finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvedPage]);

  const toggleSelected = (claim) => {
    setSelectedMap((prev) => {
      const next = new Map(prev);
      if (next.has(claim.id)) next.delete(claim.id);
      else next.set(claim.id, claim);
      return next;
    });
  };

  const selectedTotal = Array.from(selectedMap.values()).reduce((sum, c) => sum + c.totalAmountPaise, 0);

  // Fast path for the common case — no note needed to approve, so skip
  // opening the full review modal for a straightforward claim.
  const handleQuickApprove = async (claimId) => {
    setApprovingId(claimId);
    try {
      await apiService.approveExpenseClaim(claimId, token);
      toast.success("Claim approved");
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not approve this claim");
    } finally {
      setApprovingId(null);
    }
  };

  const handlePay = async () => {
    if (!selectedMap.size) {
      toast.error("Select at least one claim");
      return;
    }
    setPaying(true);
    try {
      await apiService.payExpenseClaims(Array.from(selectedMap.keys()), reference.trim(), token);
      toast.success(`${selectedMap.size} claim(s) marked paid`);
      setReference("");
      setSelectedMap(new Map());
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not run payment");
    } finally {
      setPaying(false);
    }
  };

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      <PageHead
        title="Expense Approvals"
        lede="Review claims with their receipt, linked job and the claimant's history, then approve, reject, or query. Approved claims batch into a payment run below."
        kpis={[
          { label: "Awaiting decision", value: queuePagination?.total ?? queue.length },
          { label: "Approved, awaiting payment", value: approvedPagination?.total ?? approved.length },
        ]}
      />

      <SectionTitle sub="Submitted or queried claims — approve, reject with a reason, or ask for more information.">
        Needs a decision
      </SectionTitle>

      <DashTable
        columns={[
          { label: "Claimant" },
          { label: "Items" },
          { label: "Amount", num: true },
          { label: "Dates" },
          { label: "Job" },
          { label: "Status" },
          { label: "" },
        ]}
        rows={queue.map((c) => ({
          key: c.id,
          cells: [
            c.claimantName,
            `${c.itemCount} item${c.itemCount === 1 ? "" : "s"}`,
            rupee(c.totalAmountPaise),
            c.dateFrom === c.dateTo ? shortDate(c.dateFrom) : `${shortDate(c.dateFrom)} – ${shortDate(c.dateTo)}`,
            c.orderId ? (
              <Box
                component="a"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/dashboard/orders/${c.orderId}`);
                }}
                sx={{ color: UI.p, textDecoration: "none" }}
              >
                {c.clinicName || "View order"}
              </Box>
            ) : (
              "—"
            ),
            <>
              {c.status === "queried" ? <Tag tone="s">Queried</Tag> : <Tag tone="i">Submitted</Tag>}
              {c.anyPolicyFlag ? (
                <Box sx={{ marginTop: "4px" }}>
                  <Tag tone="b">Over limit</Tag>
                </Box>
              ) : null}
            </>,
            <Box sx={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
              <Box
                component="button"
                type="button"
                onClick={() => handleQuickApprove(c.id)}
                disabled={approvingId === c.id}
                sx={{ ...btnSx("ghost"), opacity: approvingId === c.id ? 0.6 : 1 }}
              >
                {approvingId === c.id ? "Approving" : "Approve"}
              </Box>
              <Box component="button" type="button" onClick={() => setActive(c)} sx={btnSx("ghost")}>
                Review
              </Box>
            </Box>,
          ],
        }))}
        emptyText="Nothing waiting on a decision."
        errorText={loadFailed ? "Could not load claims." : null}
        onRetry={fetchAll}
      />
      <Pager
        pagination={queuePagination}
        onPrev={() => setQueuePage((p) => Math.max(p - 1, 1))}
        onNext={() => setQueuePage((p) => Math.min(p + 1, queuePagination?.totalPages || 1))}
      />

      <SectionTitle sub="Select several approved claims and pay them together under one reference.">
        Payment run
      </SectionTitle>

      <DashTable
        columns={[
          { label: "Claimant" },
          { label: "Select" },
          { label: "Items" },
          { label: "Amount", num: true },
          { label: "Dates" },
          { label: "" },
        ]}
        rows={approved.map((c) => ({
          key: c.id,
          cells: [
            c.claimantName,
            <Box
              component="input"
              type="checkbox"
              checked={selectedMap.has(c.id)}
              onChange={() => toggleSelected(c)}
              sx={{ width: 16, height: 16, accentColor: UI.p }}
            />,
            `${c.itemCount} item${c.itemCount === 1 ? "" : "s"}`,
            rupee(c.totalAmountPaise),
            c.dateFrom === c.dateTo ? shortDate(c.dateFrom) : `${shortDate(c.dateFrom)} – ${shortDate(c.dateTo)}`,
            <Box component="button" type="button" onClick={() => setActive(c)} sx={btnSx("ghost")}>
              Review
            </Box>,
          ],
        }))}
        emptyText="No approved claims waiting for payment."
      />
      <Pager
        pagination={approvedPagination}
        onPrev={() => setApprovedPage((p) => Math.max(p - 1, 1))}
        onNext={() => setApprovedPage((p) => Math.min(p + 1, approvedPagination?.totalPages || 1))}
      />

      {(approvedPagination?.total || approved.length) > 0 && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "12px",
            padding: "14px 16px",
            bgcolor: UI.surface,
            border: "1px solid",
            borderColor: UI.line,
            borderRadius: "8px",
            marginBottom: "20px",
          }}
        >
          <Box sx={{ fontSize: 13, color: UI.ink2 }}>
            {selectedMap.size} selected · {rupee(selectedTotal)}
          </Box>
          <Box
            component="input"
            placeholder="Payment reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            sx={{
              border: "1px solid",
              borderColor: UI.lineIn,
              borderRadius: "4px",
              padding: "8px 10px",
              bgcolor: UI.surface,
              color: UI.ink,
              fontSize: 13,
              fontFamily: "inherit",
              outline: "none",
              flex: "1 1 200px",
            }}
          />
          <Box
            component="button"
            type="button"
            onClick={handlePay}
            disabled={paying || !selectedMap.size}
            sx={btnSx("solid", paying || !selectedMap.size)}
          >
            {paying ? "Paying" : "Mark paid"}
          </Box>
        </Box>
      )}

      <ReviewExpenseClaimModal
        open={Boolean(active)}
        claim={active}
        onClose={() => setActive(null)}
        onSaved={() => {
          setActive(null);
          fetchAll();
        }}
        onViewOrder={(orderId) => navigate(`/dashboard/orders/${orderId}`)}
      />

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default ExpenseApprovals;
